import React from "react";
import { ServiceFormValues } from "../service-management-type";
import { useFormikContext } from "formik";
import { Chip } from "@mui/material";
import CategoryIcon from "../../../../../assets/icons/category-icon.svg";
import { KeyboardArrowDown } from "@mui/icons-material";
import { formatCurrency } from "./create-service-helper";

const ServicePreview: React.FC = () => {
  const { values } = useFormikContext<ServiceFormValues>();
  const [isSubServicesSectionOpen, setIsSubServicesSectionOpen] = React.useState<boolean>(false);
  const [isMerchandisesSectionOpen, setIsMerchandisesSectionOpen] = React.useState<boolean>(false);
  const calculateTotalPrice = () => {
    let subServicesTotal = values.subServices.reduce(
      (acc, subService) =>
        acc + (subService.minPrice + subService.maxPrice) / 2,
      0
    );
    let merchandisesTotal = values.merchandises.reduce(
      (acc, merchandise) =>
        acc + (merchandise.minPrice + merchandise.maxPrice) / 2,
      0
    );

    return (subServicesTotal + merchandisesTotal || 0).toFixed(2);
  };

  //Total value is equal to sum 
  const calculateSubServiceTotalValue = () => {
    let totalValue = values.subServices.reduce(
      (acc, subService) =>
        acc + (subService.minPrice + subService.maxPrice) / 2,
      0
    );
    return totalValue;
  };

  const calculateMerchandiseTotalValue = () => {
    let totalValue = values.merchandises.reduce(
      (acc, merchandise) =>
        acc + (merchandise.minPrice + merchandise.maxPrice) / 2,
      0
    );
    return totalValue;
  };

  const totalValue = calculateSubServiceTotalValue() + calculateMerchandiseTotalValue();
  return (
    <div className="service-preview-card">
      <div className="service-preview-header">
        <h4 className="service-preview-heading">Service Preview</h4>
        <Chip
          label={values.status ? "Active" : "Inactive"}
          size="small"
          className={`status-chip ${values.status ? "active-chip" : "inactive-chip"}`}
        />
      </div>
      <div className="service-preview-content">
        <p className="preview-label">Service Name</p>
        <p className="preview-value" title={values.name || "Not set"}>
          {values.name || "Not set"}
        </p>
        <p className="preview-label">Service Code</p>
        <p className="preview-value" title={values.code || "Not set"}>
          {values.code || "Not set"}
        </p>
        <div className="preview-row">
          <div className="preview-column">
            <p className="preview-label">Category</p>
            <p className="preview-value">
              {values?.category?.join(", ") || "Not set"}
            </p>
          </div>

          <div className="preview-column">
            <p className="preview-label">Need Type</p>
            <p className="preview-value">
              {values?.needtypes?.join(", ") || "Not set"}
            </p>
          </div>
        </div>
        <div className="separator"></div>
        <p className="preview-label">Description</p>
        <p
          className="preview-value description-value"
          title={values.description || "Not set"}
        >
          {values.description || "Not set"}
        </p>

        <div className="separator"></div>

        {/* List sub services in collapse section */}
        {values.subServices.length > 0 && (
          <>
            <div className="sub-services-list">
              <div
                className="collapsible-header"
                onClick={() =>
                  setIsSubServicesSectionOpen(!isSubServicesSectionOpen)
                }
              >
                <div className="header-text-and-arrow-icon">
                  <h4 className="sub-services-heading">Sub-Service Structure</h4>
                  <KeyboardArrowDown
                    className={`arrow-icon ${isSubServicesSectionOpen ? "expanded" : ""}`}
                  />
                </div>
                <div className="service-name-and-label">
                  <p className="service-name" title={values?.name || "Not set"}>{values?.name || "-"}</p>
                  <p className="parent-service-label">Parent Service</p>
                </div>
              </div>
              {isSubServicesSectionOpen && (
                <div className="sub-services-list-content">
                  {values.subServices.map((subService, index) => (
                    <div key={index} className="sub-service-item">
                      <div className="name-and-price">
                        <p className="sub-service-name" title={subService?.name || "Not set"}>
                          {subService.name || "Not set"}
                        </p>
                        <p className="sub-service-price">{`$${formatCurrency(subService?.minPrice)} - $${formatCurrency(subService?.maxPrice)}`}</p>
                      </div>

                      <div className="status-with-dot">
                        <p className="sub-service-label">Sub Service</p>
                        <div
                          className={`dot ${subService.status ? "active-dot" : "inactive-dot"}`}
                        ></div>
                        <span className="sub-service-status-text">{subService.status ? "Active" : "Inactive"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* List merchandises in collapse section */}
        {values?.merchandises?.length > 0 && (
          <>
            <div className="merchandises-list sub-services-list">
              <div
                className="collapsible-header"
                onClick={() =>
                  setIsMerchandisesSectionOpen(!isMerchandisesSectionOpen)
                }
              >
                <div className="header-text-and-arrow-icon">
                  <h4 className="merchandises-heading sub-services-heading">Merchandise Structure</h4>
                  <KeyboardArrowDown
                    className={`arrow-icon ${isMerchandisesSectionOpen ? "expanded" : ""}`}
                  />
                </div>
                <div className="service-name-and-label">
                  <p className="service-name" title={values?.name || "Not set"}>{values?.name || "-"}</p>
                  <p className="parent-service-label">Parent Service</p>
                </div>
              </div>
              {isMerchandisesSectionOpen && (
                <div className="merchandises-list-content sub-services-list-content">
                  {values.merchandises.map((merchandise, index) => (
                    <div key={index} className="merchandise-item sub-service-item">
                      <div className="name-and-price">
                        <p className="sub-service-price" title={merchandise?.name || "Not set"}>
                          {merchandise.name || "Not set"}
                        </p>
                        <p className="sub-service-price">{`$${formatCurrency(merchandise?.minPrice)} - $${formatCurrency(merchandise?.maxPrice)}`}</p>
                      </div>

                      <div className="status-with-dot">
                        <p className="sub-service-label">Merchandise</p>
                        <div
                          className={`dot ${merchandise.status ? "active-dot" : "inactive-dot"}`}
                        ></div>
                        <span className="sub-service-status-text">{merchandise.status ? "Active" : "Inactive"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        <div className="separator"></div>
        <p className="preview-label">Commission</p>
        <div className="row-preview-value">
          <p
            className="preview-value"
            title={
              values?.commissionPercent
                ? `${formatCurrency(values.commissionPercent)}%`
                : values?.commissionValue
                  ? `$${formatCurrency(values.commissionValue)}`
                  : "-"
            }
          >
            {values?.commissionPercent
              ? `${formatCurrency(values.commissionPercent)}%`
              : values?.commissionValue
                ? `$${formatCurrency(values.commissionValue)}`
                : "-"}
          </p>
        </div>

        <div className="separator"></div>

        <div className="taxable-row">
          <div
            className={`dot ${values.taxApplicable ? "active-dot" : "inactive-dot"}`}
          ></div>
          <p className="preview-label">Tax applicable</p>
        </div>

        <div className="preview-label-and-value-row ">
          <p className="preview-label label-with-bold">Total Price</p>
          <p
            className="preview-value value-with-bold"
            title={`$${calculateTotalPrice()}`}
          >
            {`$${formatCurrency(values?.minPrice)} - $${formatCurrency(values?.maxPrice)}`}
          </p>
        </div>

        <div className="sub-service-count-and-total-value-wrapper" style={{ flexDirection: "column" }}>
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 24 }}>
            <div className="count-div">
              <p className="count-label">Sub-Services Count</p>
              <p className="count-value">{values?.subServices?.length || 0}</p>
            </div>
            {values.subServices.length > 0 && (
              <div className="count-div">
                <p className="count-label">Total Value</p>
                <p className="count-value">${formatCurrency(calculateSubServiceTotalValue())}</p>
              </div>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 24, marginTop: 8 }}>
            <div className="count-div">
              <p className="count-label">Merchandises Count</p>
              <p className="count-value">{values?.merchandises?.length || 0}</p>
            </div>
            {values.merchandises.length > 0 && (
              <div className="count-div">
                <p className="count-label">Total Value</p>
                <p className="count-value">${formatCurrency(calculateMerchandiseTotalValue())}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicePreview;
